import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, NoticeBox, Text, theme } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import ColorPicker from 'modules/core/components/Inputs/ColorPicker'
import TextInput from 'modules/core/components/Inputs/Text'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import ShiftAccessSelectors from 'modules/shift_management/components/ShiftManagement/ShiftAccessSelectors'
import ShiftLocationPicker from 'modules/shift_management/components/ShiftManagement/ShiftLocationPicker'
import ShiftNotesField from 'modules/shift_management/components/ShiftManagement/ShiftNotesField'
import ShiftOvertimeCheckbox from 'modules/shift_management/components/ShiftManagement/ShiftOvertimeCheckbox'
import ShiftTimePicker from 'modules/shift_management/components/ShiftManagement/ShiftTimePicker'
import { SM_COLOR_CODES } from 'modules/shift_management/lib/colors'
import type DefaultShiftForm from 'modules/shift_management/lib/forms/DefaultShiftForm'
import type PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'

import ShiftsAndBreaksTimePickers from './ShiftsAndBreaksTimePickers'

interface Props {
  form: DefaultShiftForm
  isEditing?: boolean
  plannedBreakForms: PlannedBreakForm[]
  setPlannedBreakForms: React.Dispatch<React.SetStateAction<PlannedBreakForm[]>>
  onSubmit: () => void
}

const Form = ({
  form,
  isEditing = false,
  onSubmit,
  plannedBreakForms,
  setPlannedBreakForms,
}: Props) => {
  const hasColors = hasFeature('shift_management_colours_for_shifts')
  const hasLocation = hasFeature('dev_location_work_areas')
  const hasBreaks = hasFeature('time_settings_breaks')
  const isComplementaryHoursEnabled = hasFeature('dev_time_tracking_complementary_hours')
  const { startAt, endAt } = form

  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const isFormValid =
    form.isValid && plannedBreakForms.every((form) => form.isValid({ startAt, endAt }))

  return (
    <Box alignItems="center">
      <Box width="full">
        <Box gap="s16">
          <Box
            borderRadius={{ all: 'abs008' }}
            style={{ marginBottom: theme.dimensions.s32 }}
            padding="s32"
            border={{
              all: {
                style: 'solid',
                width: 's1',
                color: 'grey400',
              },
            }}
          >
            <Box gap="s16">
              <TextInput
                label={i18n.t('shift_management.default_shift.form_labels.title')}
                field={form.get('title')}
                mandatory
              />

              {hasBreaks ? (
                <ShiftsAndBreaksTimePickers
                  form={form}
                  plannedBreakForms={plannedBreakForms}
                  setPlannedBreakForms={setPlannedBreakForms}
                />
              ) : (
                <ShiftTimePicker startField={form.get('start_at')} endField={form.get('end_at')} />
              )}
              <ShiftOvertimeCheckbox
                form={form}
                isComplementaryHoursEnabled={isComplementaryHoursEnabled}
              />

              {hasColors && (
                <>
                  <Box flexDirection="row" alignItems="center">
                    <Text.Normal size="200" weight="medium" color="grey1000">
                      {i18n.t('shift_management.add_shift.color_label')}
                    </Text.Normal>
                  </Box>
                  <Box maxWidth="s600">
                    <ColorPicker
                      name="shiftColor"
                      field={form.get('color')}
                      colors={SM_COLOR_CODES}
                    />
                  </Box>
                </>
              )}

              {hasColors && isEditing && (
                <NoticeBox
                  borderRadius={{ all: 'abs008' }}
                  message={i18n.t('shift_management.add_shift.color_tooltip')}
                />
              )}
            </Box>
          </Box>

          {hasColors && hasLocation && (
            <Box alignItems="center">
              <Box width="full">
                <Box gap="s8" style={{ marginBottom: theme.dimensions.s40 }}>
                  {/* Mimicking the style of the ResponsivePage header */}
                  <Text weight="semibold" size="500" color="grey1000">
                    {i18n.t('shift_management.saved_shifts.form_location.title')}
                  </Text>
                  <Text.Normal>
                    {i18n.t('shift_management.saved_shifts.form_location.description')}
                  </Text.Normal>
                </Box>
                <Box gap="s16">
                  <Box
                    borderRadius={{ all: 'abs008' }}
                    padding="s32"
                    border={{
                      all: {
                        style: 'solid',
                        width: 's1',
                        color: 'grey400',
                      },
                    }}
                  >
                    <Box gap="s16">
                      <ShiftLocationPicker
                        locationField={form.get('location_id')}
                        workAreaField={form.get('work_area_id')}
                      />
                      <ShiftNotesField notesField={form.get('notes')} />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          <Box alignItems="center" paddingTop="s32">
            <Box width="full">
              <Box gap="s8" style={{ marginBottom: theme.dimensions.s40 }}>
                <Text weight="semibold" size="500" color="grey1000">
                  {i18n.t('shift_management.saved_shifts.form_access.title')}
                </Text>
                <Text.Normal>
                  {i18n.t('shift_management.saved_shifts.form_access.description_2')}
                </Text.Normal>
              </Box>
              <Box gap="s16">
                <Box
                  borderRadius={{ all: 'abs008' }}
                  padding="s32"
                  border={{
                    all: {
                      style: 'solid',
                      width: 's1',
                      color: 'grey400',
                    },
                  }}
                >
                  <Box gap="s16">
                    <ShiftAccessSelectors form={form} />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box justifyContent="flexEnd" gap="s8" flexDirection="row">
            <Button
              type="secondary"
              onClick={() => navigate(navigation.shifts.savedShifts.fullPath())}
            >
              {i18n.t('common.cancel')}
            </Button>
            <Button type="primary" onClick={onSubmit} disabled={!isFormValid}>
              {i18n.t('common.submit')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default observer(Form)
